import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { usePerfMode } from '../hooks/usePerfMode';
import { hideOnMobile, pauseAnimationsOnMobile, respectReducedMotion, solidGlassOnMobile } from '../styles/performance';

const ACCENT = '#b388ff';
const ACCENT_DIM = 'rgba(179,136,255,0.35)';

const orbDrift = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(20px, -15px) scale(1.04); }
`;

const auroraPulse = keyframes`
  0%, 100% { opacity: 0.35; }
  50%       { opacity: 0.55; }
`;

const gridScroll = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 0 120px; }
`;

const gridScrollReverse = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 0 -80px; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const lineGrow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.9; }
  50%       { opacity: 1; }
`;

const shineSweep = keyframes`
  0%   { background-position: -150% 0; }
  100% { background-position: 250% 0; }
`;

/* ─── Section shell ─── */
const ProductsContainer = styled.section`
  padding: 6rem 20px 8rem;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  color: #fff;
`;

const AuroraLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle, ${({ $color }) => $color} 0%, transparent 70%);
  filter: blur(70px);
  animation: ${orbDrift} ${({ $dur }) => $dur}s ease-in-out infinite,
             ${auroraPulse} ${({ $dur }) => $dur * 1.2}s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  ${pauseAnimationsOnMobile}
  ${respectReducedMotion}
`;

const GridFloor = styled.div`
  position: absolute;
  bottom: 0;
  left: -50%;
  right: -50%;
  z-index: 1;
  height: 62%;
  background-image:
    linear-gradient(rgba(179,136,255,0.2) 1px, transparent 1px),
    linear-gradient(90deg, rgba(179,136,255,0.2) 1px, transparent 1px);
  background-size: 48px 48px;
  transform: perspective(500px) rotateX(65deg);
  transform-origin: center top;
  mask-image: linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 55%, transparent 88%);
  animation: ${gridScroll} 2.2s linear infinite;
  pointer-events: none;
  filter: drop-shadow(0 0 8px rgba(179,136,255,0.15));
  ${pauseAnimationsOnMobile}
  ${respectReducedMotion}
`;

const GridFloorGlow = styled.div`
  position: absolute;
  bottom: 0;
  left: -50%;
  right: -50%;
  z-index: 1;
  height: 55%;
  background-image:
    linear-gradient(rgba(179,136,255,0.08) 2px, transparent 2px),
    linear-gradient(90deg, rgba(179,136,255,0.08) 2px, transparent 2px);
  background-size: 96px 96px;
  transform: perspective(500px) rotateX(65deg);
  transform-origin: center top;
  mask-image: linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 80%);
  animation: ${gridScrollReverse} 3.5s linear infinite;
  pointer-events: none;
  ${hideOnMobile}
  ${respectReducedMotion}
`;

const Vignette = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.55) 100%);
  pointer-events: none;
`;

const Wrapper = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

/* ─── Header ─── */
const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
  opacity: 0;
  animation: ${fadeIn} 0.9s ease forwards 0.15s;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  span {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.65);
  }
`;

const EyebrowLine = styled.div`
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, transparent, ${ACCENT});
  transform-origin: right;
  animation: ${lineGrow} 0.8s ease forwards 0.4s;
  transform: scaleX(0);

  &:last-of-type {
    background: linear-gradient(90deg, ${ACCENT}, transparent);
    transform-origin: left;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 900;
  margin: 0 0 1rem;
  letter-spacing: 0.04em;
  background: linear-gradient(120deg, #fff 0%, ${ACCENT} 45%, #fff 80%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 4s linear infinite;
`;

const SectionSubtitle = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.15rem);
  color: rgba(200, 200, 220, 0.75);
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.65;
`;

const CarouselHint = styled.p`
  text-align: center;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(200,200,220,0.45);
  margin-top: 1.5rem;
  opacity: 0;
  animation: ${fadeIn} 0.8s ease forwards 0.6s;
`;

/* ─── Carousel (logic unchanged) ─── */
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 580px;
  perspective: 1200px;
  transform-style: preserve-3d;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  opacity: 0;
  animation: ${fadeIn} 1s ease forwards 0.35s;

  @media (max-width: 1024px) { height: 500px; }
  @media (max-width: 768px)  { height: 460px; }
`;

const Carousel = styled.div`
  position: relative;
  width: var(--carousel-radius);
  height: var(--carousel-radius);
  padding-top: 40px;
  transform-style: preserve-3d;
  transition: transform 0.5s ease;

  @media (max-width: 1024px) { padding-top: 28px; }
  @media (max-width: 768px)  { padding-top: 20px; }
`;

const Card = styled.div`
  position: absolute;
  width: var(--card-width);
  height: var(--card-height);
  left: 50%;
  top: 50%;
  margin-left: calc(var(--card-width) / -2);
  margin-top: calc(var(--card-height) / -2);
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
`;

const Inner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${Card}.flipped & { transform: rotateY(180deg); }
`;

const Face = styled.div`
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: 20px;
  overflow: hidden;
`;

const glassFace = css`
  background: radial-gradient(140% 140% at 10% 10%, rgba(179,136,255,0.12), rgba(12,10,24,0.75));
  border: 1px solid rgba(179,136,255,0.35);
  box-shadow:
    0 16px 48px rgba(0,0,0,0.45),
    0 0 0 1px rgba(255,255,255,0.04) inset;
  backdrop-filter: blur(14px);
  ${solidGlassOnMobile}
`;

const Front = styled(Face)`
  ${glassFace}
  z-index: 2;
  ${Card}.flipped & { z-index: 1; }
`;

const Back = styled(Face)`
  ${glassFace}
  border-color: rgba(179,136,255,0.5);
  transform: rotateY(180deg);
  z-index: 1;
  ${Card}.flipped & { z-index: 2; }
`;

const Content = styled.div`
  padding: 18px;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;
`;

const Tag = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${ACCENT};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(179,136,255,0.12);
  border: 1px solid rgba(179,136,255,0.25);
  align-self: flex-start;
`;

const Title = styled.h3`
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0;
  color: #fff;
  letter-spacing: 0.03em;
`;

const ImageBox = styled.div`
  width: 100%;
  height: 130px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: rgba(0,0,0,0.35);
  border: 1px solid rgba(179,136,255,0.15);

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: drop-shadow(0 0 12px ${ACCENT_DIM});
    animation: ${pulse} 3s ease-in-out infinite;
  }
`;

const ImageShine = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(179,136,255,0.12) 50%, transparent 60%);
  background-size: 200% 100%;
  animation: ${shineSweep} 3.5s ease-in-out infinite;
  pointer-events: none;
`;

const VisitButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid rgba(179,136,255,0.4);
  color: #fff;
  text-decoration: none;
  background: linear-gradient(135deg, rgba(179,136,255,0.2), rgba(107,56,166,0.15));
  box-shadow: 0 4px 20px rgba(179,136,255,0.2);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  font-weight: 600;
  font-size: 0.9rem;
  letter-spacing: 0.04em;
  margin-top: auto;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(179,136,255,0.35);
    border-color: ${ACCENT};
  }
`;

const BackText = styled.p`
  font-size: 0.92rem;
  color: rgba(200, 200, 220, 0.85);
  margin: 0;
  line-height: 1.6;
  flex-grow: 1;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(179,136,255,0.3);
    border-radius: 4px;
  }
`;

const FlipHint = styled.span`
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(179,136,255,0.6);
  margin-top: 8px;
`;

const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 20px;
  pointer-events: none;
  background: radial-gradient(circle at 50% 0%, rgba(179,136,255,0.15), transparent 65%);
  opacity: 0;
  transition: opacity 0.35s ease;
  ${Card}:hover & { opacity: 1; }
`;

const Controls = styled.div`
  position: absolute;
  bottom: -100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 3;
`;

const ControlBtn = styled.button`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(179,136,255,0.4);
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  font-size: 1.4rem;
  line-height: 1;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  backdrop-filter: blur(8px);

  &:hover {
    transform: scale(1.08);
    border-color: ${ACCENT};
    box-shadow: 0 0 20px rgba(179,136,255,0.3);
  }
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '24px' : '8px')};
  height: 8px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${({ $active }) => ($active ? ACCENT : 'rgba(179,136,255,0.25)')};
  box-shadow: ${({ $active }) => ($active ? `0 0 12px ${ACCENT_DIM}` : 'none')};
  transition: width 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
`;

const ORBS = [
  { top: '5%',  left: '10%', size: 320, color: 'rgba(139,92,246,0.4)',  dur: 16, delay: 0 },
  { top: '70%', left: '75%', size: 280, color: 'rgba(179,136,255,0.3)', dur: 20, delay: 3 },
  { top: '40%', left: '50%', size: 200, color: 'rgba(99,60,180,0.25)',  dur: 14, delay: 1 },
];

const useCSSVars = (radius) => {
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--card-width', window.innerWidth <= 576 ? '220px' : window.innerWidth <= 768 ? '250px' : '300px');
    root.style.setProperty('--card-height', window.innerWidth <= 576 ? '260px' : window.innerWidth <= 768 ? '290px' : '320px');
    root.style.setProperty('--carousel-radius', `${radius}px`);
  }, [radius]);
};

const ProductsSection = ({ sharedBackdrop = false }) => {
  const carouselRef = useRef(null);
  const cardRefs = useRef([]);
  const { lite } = usePerfMode();
  const [theta, setTheta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(0);
  const currentIndexRef = useRef(0);
  const [radius, setRadius] = useState(window.innerWidth <= 576 ? 240 : window.innerWidth <= 768 ? 280 : 400);

  useCSSVars(radius);

  const products = useMemo(() => ([
    {
      id: 1,
      tag: 'Education Technology',
      title: "Champs' Chaps",
      description: "A scalable SaaS infrastructure enabling educators and institutions to build and manage LMS-driven digital education platforms with minimal operational dependency. Evolving toward hybrid educational governance integrating digital learning, institutional management, hybrid classroom infrastructure, educator monetization, and student governance systems.",
      image: '/images/champslatest.png',
      url: 'https://champschaps.com/'
    },
    {
      id: 2,
      tag: 'Hospitality & Vendor Governance',
      title: 'The-HEY',
      description: "Digital governance integrated with food court and vendor operations. Includes sales tracking, menu management, KOT/BOT operations, vendor governance, customer login systems, shop assistant operations, ground-level manager reporting, investor visibility dashboards, and sales analytics — enabling vendors, managers, investors, and staff within one measurable ecosystem.",
      image: '/images/heylatest.png',
      url: 'https://www.the-hey.com/'
    },
    {
      id: 3,
      tag: 'Hybrid Corporate Spaces',
      title: 'Optical Spaces',
      description: "Hybrid operational infrastructure for startups, entrepreneurs, and corporate entities. Integrates physical office infrastructure with HRM systems, project management, task governance, operational structuring, and hybrid workspace management — combining measurable physical workspace allocation with digitally governed operational systems.",
      image: '/images/opticallatest.png',
      url: 'https://www.opticalspaces.com/'
    }
  ]), []);

  const anglePerCard = 360 / products.length;
  const currentIndex = ((Math.round(Math.abs(theta / anglePerCard)) % products.length) + products.length) % products.length;

  const arrangeCards = useCallback(() => {
    cardRefs.current.forEach((el, idx) => {
      if (!el) return;
      const cardAngle = anglePerCard * idx;
      el.style.transform = `rotateY(${cardAngle}deg) translateZ(${radius}px)`;
      el.dataset.index = String(idx);
    });
  }, [anglePerCard, radius]);

  const rotateCarousel = useCallback((angle) => {
    if (!carouselRef.current) return;
    carouselRef.current.style.transform = `rotateY(${angle}deg)`;
    const rawIndex = Math.round(Math.abs(angle / anglePerCard)) % products.length;
    currentIndexRef.current = rawIndex;
  }, [anglePerCard, products.length]);

  const goToIndex = useCallback((index) => {
    const target = -anglePerCard * index;
    setTheta(target);
    rotateCarousel(target);
  }, [anglePerCard, rotateCarousel]);

  const nextCard = useCallback(() => {
    setTheta((prev) => {
      const next = prev - anglePerCard;
      rotateCarousel(next);
      return next;
    });
  }, [anglePerCard, rotateCarousel]);

  const prevCard = useCallback(() => {
    setTheta((prev) => {
      const next = prev + anglePerCard;
      rotateCarousel(next);
      return next;
    });
  }, [anglePerCard, rotateCarousel]);

  const onFlip = useCallback((e) => {
    if (e.target.closest('a')) return;
    const card = e.currentTarget;
    const cardIndex = parseInt(card.dataset.index || '0', 10);
    if (cardIndex === currentIndexRef.current) {
      card.classList.toggle('flipped');
    }
  }, []);

  const onVisitClick = useCallback((e, url) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const dragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = ('touches' in e) ? e.touches[0].pageX : e.pageX;
  }, []);

  const dragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const currentX = ('touches' in e) ? e.touches[0].pageX : e.pageX;
    const diffX = currentX - startXRef.current;
    const sensitivity = 0.5;
    const newTheta = theta + diffX * sensitivity;
    if (carouselRef.current) {
      carouselRef.current.style.transform = `rotateY(${newTheta}deg)`;
    }
  }, [isDragging, theta]);

  const dragEnd = useCallback((e) => {
    if (!isDragging) return;
    setIsDragging(false);
    const currentX = ('changedTouches' in e) ? e.changedTouches[0].pageX : e.pageX;
    const diffX = currentX - startXRef.current;
    if (Math.abs(diffX) > 20) {
      if (diffX > 0) prevCard(); else nextCard();
    } else {
      const snap = Math.round(theta / anglePerCard) * anglePerCard;
      setTheta(snap);
      rotateCarousel(snap);
    }
  }, [isDragging, theta, anglePerCard, rotateCarousel, nextCard, prevCard]);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') nextCard();
    else if (e.key === 'ArrowRight') prevCard();
    else if (e.key === 'Enter' || e.key === ' ') {
      const idx = currentIndexRef.current;
      const el = cardRefs.current[idx];
      if (el) el.classList.toggle('flipped');
    }
  }, [nextCard, prevCard]);

  useEffect(() => {
    arrangeCards();
    rotateCarousel(theta);
  }, [arrangeCards, rotateCarousel, theta]);

  useEffect(() => {
    const onResize = () => {
      const r = window.innerWidth <= 576 ? 240 : window.innerWidth <= 768 ? 280 : 400;
      setRadius(r);
      arrangeCards();
      rotateCarousel(theta);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [arrangeCards, rotateCarousel, theta]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    el.addEventListener('mousedown', dragStart);
    el.addEventListener('touchstart', dragStart, { passive: true });
    document.addEventListener('mousemove', dragMove);
    document.addEventListener('touchmove', dragMove, { passive: false });
    document.addEventListener('mouseup', dragEnd);
    document.addEventListener('touchend', dragEnd);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      el.removeEventListener('mousedown', dragStart);
      el.removeEventListener('touchstart', dragStart);
      document.removeEventListener('mousemove', dragMove);
      document.removeEventListener('touchmove', dragMove);
      document.removeEventListener('mouseup', dragEnd);
      document.removeEventListener('touchend', dragEnd);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dragStart, dragMove, dragEnd, onKeyDown]);

  const visibleOrbs = lite ? ORBS.slice(0, 1) : ORBS;

  return (
    <ProductsContainer id="ecosystems">
      {!sharedBackdrop && (
        <>
          <AuroraLayer>
            {visibleOrbs.map((o, i) => (
              <Orb
                key={i}
                $color={o.color}
                $dur={o.dur}
                $delay={o.delay}
                style={{ top: o.top, left: o.left, width: o.size, height: o.size }}
              />
            ))}
          </AuroraLayer>
          <GridFloorGlow />
          <GridFloor />
          <Vignette />
        </>
      )}

      <Wrapper>
        <SectionHeader>
          <Eyebrow>
            <EyebrowLine />
            <span>The Future We Build</span>
            <EyebrowLine />
          </Eyebrow>
          <SectionTitle>Our Projects</SectionTitle>
          <SectionSubtitle>
            Scalable SaaS and hybrid operational platforms transforming education,
            hospitality, and corporate workspaces.
          </SectionSubtitle>
        </SectionHeader>

        <CarouselContainer>
          <Carousel
            ref={carouselRef}
            style={{ '--carousel-radius': `${radius}px`, '--card-width': '300px', '--card-height': '333px' }}
          >
            {products.map((p, i) => (
              <Card
                key={p.id}
                ref={(el) => (cardRefs.current[i] = el)}
                onClick={onFlip}
              >
                <Inner className="Inner">
                  <Front>
                    <Content>
                      <Tag>{p.tag}</Tag>
                      <Title>{p.title}</Title>
                      <ImageBox>
                        <img src={p.image} alt={p.title} loading="lazy" decoding="async" width="300" height="130" />
                        <ImageShine />
                      </ImageBox>
                      <VisitButton
                        href={p.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                          e.preventDefault();
                          onVisitClick(e, p.url);
                        }}
                      >
                        Visit Site →
                      </VisitButton>
                      <CardGlow />
                    </Content>
                  </Front>
                  <Back>
                    <Content>
                      <Tag>Overview</Tag>
                      <Title>{p.title}</Title>
                      <BackText>{p.description}</BackText>
                      <FlipHint>Tap front to flip back</FlipHint>
                    </Content>
                  </Back>
                </Inner>
              </Card>
            ))}
          </Carousel>

          <Controls>
            <ControlBtn onClick={prevCard} aria-label="Previous">‹</ControlBtn>
            <Dots>
              {products.map((p, i) => (
                <Dot
                  key={p.id}
                  $active={i === currentIndex}
                  onClick={() => goToIndex(i)}
                  aria-label={`Go to ${p.title}`}
                />
              ))}
            </Dots>
            <ControlBtn onClick={nextCard} aria-label="Next">›</ControlBtn>
          </Controls>
        </CarouselContainer>

        <CarouselHint>Drag to rotate · Click active card to flip · Arrow keys supported</CarouselHint>
      </Wrapper>
    </ProductsContainer>
  );
};

export default ProductsSection;
